import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import CurrencyFormat from "react-currency-format";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#0088FE', '#00C49F'];

const PackageWiseSalesReport = ({ salesData }) => {
  // Top 10 Packages by Sales
  const topPackages = useMemo(() => {
    const packageMap = {};
    salesData.forEach((item) => {
      const pkgName = item.package_name || "Unknown";
      const categoryName = item.category_name || "Uncategorized";
      
      if (!packageMap[pkgName]) {
        packageMap[pkgName] = {
          name: pkgName,
          category: categoryName,
          sales: 0,
          quantity: 0,
          netPrice: 0,
          discount: 0,
          tax: 0,
          newSales: 0,
          renewSales: 0,
        };
      }
      packageMap[pkgName].sales += parseFloat(item.order_detail_price || 0);
      packageMap[pkgName].quantity += 1;
      packageMap[pkgName].netPrice += parseFloat(item.order_detail_net_price || 0);
      packageMap[pkgName].discount += parseFloat(item.order_detail_discount || 0);
      packageMap[pkgName].tax += parseFloat(item.order_detail_tax || 0);
      
      if (item.sale_type === "New") {
        packageMap[pkgName].newSales += parseFloat(item.order_detail_net_price || 0);
      } else if (item.sale_type === "Renew") {
        packageMap[pkgName].renewSales += parseFloat(item.order_detail_net_price || 0);
      }
    });
    
    return Object.values(packageMap)
      .sort((a, b) => b.netPrice - a.netPrice)
      .slice(0, 10)
      .map((pkg, index) => ({
        ...pkg,
        rank: index + 1,
        displayName: pkg.name.length > 25 ? pkg.name.substring(0, 25) + "..." : pkg.name,
      }));
  }, [salesData]);

  // Sales by Category
  const categorySalesData = useMemo(() => {
    const categoryMap = {};
    salesData.forEach((item) => {
      const categoryName = item.category_name || "Uncategorized";
      if (!categoryMap[categoryName]) {
        categoryMap[categoryName] = {
          name: categoryName,
          sales: 0,
          quantity: 0,
          netPrice: 0,
          packages: new Set(),
        };
      }
      categoryMap[categoryName].sales += parseFloat(item.order_detail_price || 0);
      categoryMap[categoryName].quantity += 1;
      categoryMap[categoryName].netPrice += parseFloat(item.order_detail_net_price || 0);
      if (item.package_name) {
        categoryMap[categoryName].packages.add(item.package_name);
      }
    });
    
    return Object.values(categoryMap)
      .map(cat => ({
        ...cat,
        packageCount: cat.packages.size,
      }))
      .sort((a, b) => b.netPrice - a.netPrice);
  }, [salesData]);

  // Top Packages by Category
  const topPackagesByCategory = useMemo(() => {
    const categoryPackageMap = {};
    
    salesData.forEach((item) => {
      const categoryName = item.category_name || "Uncategorized";
      const pkgName = item.package_name || "Unknown";
      const key = `${categoryName}_${pkgName}`;
      
      if (!categoryPackageMap[key]) {
        categoryPackageMap[key] = {
          category: categoryName,
          package: pkgName,
          sales: 0,
          quantity: 0,
          netPrice: 0,
        };
      }
      categoryPackageMap[key].sales += parseFloat(item.order_detail_price || 0);
      categoryPackageMap[key].quantity += 1;
      categoryPackageMap[key].netPrice += parseFloat(item.order_detail_net_price || 0);
    });
    
    // Group by category and get top 3 packages per category
    const categoryGroups = {};
    Object.values(categoryPackageMap).forEach(item => {
      if (!categoryGroups[item.category]) {
        categoryGroups[item.category] = [];
      }
      categoryGroups[item.category].push(item);
    });
    
    const result = {};
    Object.keys(categoryGroups).forEach(category => {
      result[category] = categoryGroups[category]
        .sort((a, b) => b.netPrice - a.netPrice)
        .slice(0, 3)
        .map((pkg, idx) => ({
          ...pkg,
          displayPackage: pkg.package.length > 20 ? pkg.package.substring(0, 20) + "..." : pkg.package,
        }));
    });
    
    return result;
  }, [salesData]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded shadow">
          <p className="fw-bold mb-1">{payload[0].name}</p>
          {payload.map((entry, index) => (
            <p key={index} className="mb-0" style={{ color: entry.color }}>
              {entry.dataKey}: <CurrencyFormat
                value={entry.value}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"Rs "}
              />
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-12 col-md-3 mb-3">
          <div className="card bg-primary text-white">
            <div className="card-body text-center">
              <h6 className="card-title mb-1">Total Packages</h6>
              <h4 className="mb-0">{topPackages.length}</h4>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <h6 className="card-title mb-1">Total Sales</h6>
              <h5 className="mb-0">
                <CurrencyFormat
                  value={topPackages.reduce((sum, pkg) => sum + pkg.netPrice, 0)}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={"Rs "}
                />
              </h5>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <h6 className="card-title mb-1">Total Quantity</h6>
              <h4 className="mb-0">{topPackages.reduce((sum, pkg) => sum + pkg.quantity, 0)}</h4>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-3 mb-3">
          <div className="card bg-warning text-white">
            <div className="card-body text-center">
              <h6 className="card-title mb-1">Categories</h6>
              <h4 className="mb-0">{categorySalesData.length}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Top 10 Packages Table */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-trophy me-2"></i>Top 10 Packages by Sales
          </h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-striped">
              <thead className="table-light">
                <tr>
                  <th>Rank</th>
                  <th>Package Name</th>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Total Price</th>
                  <th>Total Discount</th>
                  <th>Total Tax</th>
                  <th>Total Net Price</th>
                  <th>New Sales</th>
                  <th>Renew Sales</th>
                </tr>
              </thead>
              <tbody>
                {topPackages.map((pkg, index) => (
                  <tr key={index}>
                    <td>
                      <span className={`badge ${index < 3 ? 'bg-warning' : 'bg-secondary'}`}>
                        #{pkg.rank}
                      </span>
                    </td>
                    <td><strong>{pkg.name}</strong></td>
                    <td>{pkg.category}</td>
                    <td>{pkg.quantity}</td>
                    <td>
                      <CurrencyFormat
                        value={pkg.sales}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                      />
                    </td>
                    <td>
                      <CurrencyFormat
                        value={pkg.discount}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                      />
                    </td>
                    <td>
                      <CurrencyFormat
                        value={pkg.tax}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                      />
                    </td>
                    <td>
                      <strong>
                        <CurrencyFormat
                          value={pkg.netPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </strong>
                    </td>
                    <td>
                      <span className="badge bg-success">
                        <CurrencyFormat
                          value={pkg.newSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-info">
                        <CurrencyFormat
                          value={pkg.renewSales}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-success fw-bold">
                <tr>
                  <td colSpan={4} className="text-end"><strong>Grand Total:</strong></td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.sales, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.discount, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.tax, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.netPrice, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.newSales, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={topPackages.reduce((sum, pkg) => sum + pkg.renewSales, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Graphical Views */}
      <div className="row mb-4">
        {/* Top 10 Packages Bar Chart */}
        <div className="col-12 col-lg-6 mb-4">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h6 className="mb-0">
                <i className="bi bi-bar-chart me-2"></i>Top 10 Packages - Net Sales
              </h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={topPackages}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="displayName"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#0088FE" name="Net Price">
                    {topPackages.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top 10 Packages Pie Chart */}
        <div className="col-12 col-lg-6 mb-4">
          <div className="card">
            <div className="card-header bg-success text-white">
              <h6 className="mb-0">
                <i className="bi bi-pie-chart me-2"></i>Top 10 Packages - Sales Distribution
              </h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={topPackages}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name.substring(0, 15)}... ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="netPrice"
                  >
                    {topPackages.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Category-wise Sales */}
      <div className="row mb-4">
        {/* Category Sales Bar Chart */}
        <div className="col-12 col-lg-6 mb-4">
          <div className="card">
            <div className="card-header bg-info text-white">
              <h6 className="mb-0">
                <i className="bi bi-bar-chart me-2"></i>Sales by Category
              </h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={categorySalesData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={120} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="netPrice" fill="#00C49F" name="Net Price">
                    {categorySalesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Category Sales Pie Chart */}
        <div className="col-12 col-lg-6 mb-4">
          <div className="card">
            <div className="card-header bg-warning text-white">
              <h6 className="mb-0">
                <i className="bi bi-pie-chart me-2"></i>Category Sales Distribution
              </h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={categorySalesData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="netPrice"
                  >
                    {categorySalesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Category-wise Package Breakdown */}
      <div className="card mb-4">
        <div className="card-header bg-secondary text-white">
          <h5 className="mb-0">
            <i className="bi bi-layers me-2"></i>Top Packages by Category
          </h5>
        </div>
        <div className="card-body">
          {Object.keys(topPackagesByCategory).sort().map((category) => (
            <div key={category} className="mb-4">
              <h6 className="text-primary mb-3">
                <i className="bi bi-tag-fill me-2"></i>{category}
              </h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Package Name</th>
                      <th>Quantity</th>
                      <th>Total Price</th>
                      <th>Total Net Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topPackagesByCategory[category].map((pkg, idx) => (
                      <tr key={idx}>
                        <td><strong>{pkg.package}</strong></td>
                        <td>{pkg.quantity}</td>
                        <td>
                          <CurrencyFormat
                            value={pkg.sales}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"Rs "}
                          />
                        </td>
                        <td>
                          <strong>
                            <CurrencyFormat
                              value={pkg.netPrice}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"Rs "}
                            />
                          </strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-info fw-bold">
                    <tr>
                      <td className="text-end"><strong>Category Total:</strong></td>
                      <td>
                        <strong>{topPackagesByCategory[category].reduce((sum, pkg) => sum + pkg.quantity, 0)}</strong>
                      </td>
                      <td>
                        <CurrencyFormat
                          value={topPackagesByCategory[category].reduce((sum, pkg) => sum + pkg.sales, 0)}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                      <td>
                        <CurrencyFormat
                          value={topPackagesByCategory[category].reduce((sum, pkg) => sum + pkg.netPrice, 0)}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Summary Table */}
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-list-check me-2"></i>Category Summary
          </h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover table-striped">
              <thead className="table-light">
                <tr>
                  <th>Category</th>
                  <th>Package Count</th>
                  <th>Total Quantity</th>
                  <th>Total Sales</th>
                  <th>Total Net Price</th>
                </tr>
              </thead>
              <tbody>
                {categorySalesData.map((cat, index) => (
                  <tr key={index}>
                    <td><strong>{cat.name}</strong></td>
                    <td>{cat.packageCount}</td>
                    <td>{cat.quantity}</td>
                    <td>
                      <CurrencyFormat
                        value={cat.sales}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"Rs "}
                      />
                    </td>
                    <td>
                      <strong>
                        <CurrencyFormat
                          value={cat.netPrice}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"Rs "}
                        />
                      </strong>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-success fw-bold">
                <tr>
                  <td className="text-end"><strong>Grand Total:</strong></td>
                  <td>
                    <strong>{categorySalesData.reduce((sum, cat) => sum + cat.packageCount, 0)}</strong>
                  </td>
                  <td>
                    <strong>{categorySalesData.reduce((sum, cat) => sum + cat.quantity, 0)}</strong>
                  </td>
                  <td>
                    <CurrencyFormat
                      value={categorySalesData.reduce((sum, cat) => sum + cat.sales, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                  <td>
                    <CurrencyFormat
                      value={categorySalesData.reduce((sum, cat) => sum + cat.netPrice, 0)}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"Rs "}
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageWiseSalesReport;

